import type { Scene } from '@babylonjs/core/scene';
import { Color3 } from '@babylonjs/core/Maths/math.color';
import { PBRMaterial } from '@babylonjs/core/Materials/PBR/pbrMaterial';
import { DynamicTexture } from '@babylonjs/core/Materials/Textures/dynamicTexture';
import { Texture } from '@babylonjs/core/Materials/Textures/texture';
import type { GreyboxMaterialId } from '../assets/AssetTypes';

type Pattern =
  | { readonly kind: 'grid'; readonly line: string; readonly tileMeters: number }
  | { readonly kind: 'stripes'; readonly second: string; readonly tileMeters: number }
  | { readonly kind: 'ribs'; readonly line: string; readonly tileMeters: number }
  /** `count` bars across the tile along v – the lugs of a rubber belt. */
  | { readonly kind: 'lugs'; readonly line: string; readonly count: number; readonly tileMeters: number };

interface MaterialSpec {
  readonly color: string;
  readonly emissive?: string;
  readonly pattern?: Pattern;
  /** Overrides the matte default (e.g. wet puddles). */
  readonly roughness?: number;
  /** Metal instead of plastic (default 0) – mirrors the sky like real gold. */
  readonly metallic?: number;
  /** Not frozen: the game moves its texture (a scrolling belt) – a frozen material keeps its texture matrix. */
  readonly dynamic?: boolean;
}

/**
 * Greybox palette. Readable, flat colors; procedural patterns give scale and
 * speed perception. UVs of greybox parts are in meters (see GreyboxFactory),
 * so `tileMeters` is the real-world size of one texture tile.
 * Physically based (matte plastic look) so placeholders sit in the same light
 * as the exported PBR assets (sun, sky reflections, tone mapping).
 */
const ROUGHNESS = 0.75;
const SPECS: Readonly<Record<GreyboxMaterialId, MaterialSpec>> = {
  track: { color: '#686d75', pattern: { kind: 'grid', line: '#7b818a', tileMeters: 2 } },
  trackSide: { color: '#8c7b62', pattern: { kind: 'grid', line: '#7e6f58', tileMeters: 4 } },
  laneLine: { color: '#f4f4f4', emissive: '#555555' },
  curb: { color: '#b9b8b0', pattern: { kind: 'grid', line: '#a3a29a', tileMeters: 1 } },
  fence: { color: '#3f6d57', pattern: { kind: 'ribs', line: '#335a47', tileMeters: 0.25 } },
  fencePost: { color: '#9aa1a8' },
  barrier: { color: '#d63a32', pattern: { kind: 'stripes', second: '#f5f5f5', tileMeters: 0.5 } },
  chevron: { color: '#f5c400', emissive: '#3a2e00', pattern: { kind: 'stripes', second: '#1d1d1d', tileMeters: 0.6 } },
  concrete: { color: '#a9a69e', pattern: { kind: 'grid', line: '#97948c', tileMeters: 1 } },
  wood: { color: '#b58a52', pattern: { kind: 'ribs', line: '#9c7444', tileMeters: 0.15 } },
  steel: { color: '#8d959d' },
  container: { color: '#c65a2c', pattern: { kind: 'ribs', line: '#a94a23', tileMeters: 0.3 } },
  containerAlt: { color: '#3c6ea6', pattern: { kind: 'ribs', line: '#325c8c', tileMeters: 0.3 } },
  bottle: { color: '#9a5410', emissive: '#4a2400' },
  bottleCap: { color: '#e0e0e0', emissive: '#303030' },
  // polished gold: reflects the evening sky (SEG_GOLDEN_RAMP_60M "glänzt"), warm glow in the shade
  golden: { color: '#ffcc2e', emissive: '#7a5a00', roughness: 0.28, metallic: 1 },
  crate: { color: '#2f8a3a', emissive: '#0d2a10' },
  heart: { color: '#e8394f', emissive: '#6a0d1a' },
  magnet: { color: '#c62828', emissive: '#4a0a0a' },
  timeBonus: { color: '#2bb6f6', emissive: '#0a4a6a' },
  workerVest: { color: '#ff7a00', emissive: '#4a2000' },
  workerShirt: { color: '#4b5662' },
  workerPants: { color: '#2e4b7c' },
  workerHelmet: { color: '#f6c600', emissive: '#3a2e00' },
  skin: { color: '#deb08a' },
  // Enclosed modules: lamp glass glows (also at night), puddles reflect the lamps.
  lamp: { color: '#fff3d6', emissive: '#ffd99a' },
  water: { color: '#3a4652', roughness: 0.06 },
  concreteDark: { color: '#6f6c66', pattern: { kind: 'grid', line: '#625f59', tileMeters: 1.5 } },
  drywall: { color: '#d8d4cb', pattern: { kind: 'grid', line: '#c4bfb3', tileMeters: 1.25 } },
  // Machines: construction-yellow paint, black rubber; the belt's UVs follow the belt contract (one tile = 4 lugs)
  machineYellow: { color: '#f2b705', roughness: 0.55 },
  rubberBlack: { color: '#1f1f1f', roughness: 0.9 },
  dozerTrack: { color: '#2c2c2c', pattern: { kind: 'lugs', line: '#0f0f0f', count: 4, tileMeters: 1 }, roughness: 0.95, dynamic: true },
  missing: { color: '#ff00ff', emissive: '#ff00ff' },
};

const TEXTURE_SIZE = 128;

export class GreyboxMaterials {
  private readonly cache = new Map<GreyboxMaterialId, PBRMaterial>();

  constructor(private readonly scene: Scene) {}

  get(id: GreyboxMaterialId): PBRMaterial {
    let material = this.cache.get(id);
    if (!material) {
      material = this.create(id, SPECS[id]);
      this.cache.set(id, material);
    }
    return material;
  }

  private create(id: GreyboxMaterialId, spec: MaterialSpec): PBRMaterial {
    const material = new PBRMaterial(`MAT_GREYBOX_${id}`, this.scene);
    material.albedoColor = Color3.FromHexString(spec.color).toLinearSpace();
    material.metallic = spec.metallic ?? 0;
    material.roughness = spec.roughness ?? ROUGHNESS;
    if (spec.emissive) material.emissiveColor = Color3.FromHexString(spec.emissive).toLinearSpace();
    if (spec.pattern) {
      material.albedoTexture = this.createPatternTexture(id, spec.color, spec.pattern);
      material.albedoColor = Color3.White();
    }
    if (!spec.dynamic) material.freeze();
    return material;
  }

  private createPatternTexture(id: string, base: string, pattern: Pattern): DynamicTexture {
    const texture = new DynamicTexture(`TEX_GREYBOX_${id}`, TEXTURE_SIZE, this.scene, true);
    const ctx = texture.getContext();
    const s = TEXTURE_SIZE;
    ctx.fillStyle = base;
    ctx.fillRect(0, 0, s, s);
    switch (pattern.kind) {
      case 'grid':
        ctx.strokeStyle = pattern.line;
        ctx.lineWidth = 4;
        ctx.strokeRect(0, 0, s, s);
        break;
      case 'ribs':
        ctx.fillStyle = pattern.line;
        ctx.fillRect(0, 0, s * 0.25, s);
        break;
      case 'lugs':
        ctx.fillStyle = pattern.line;
        for (let i = 0; i < pattern.count; i++) ctx.fillRect(0, (i * s) / pattern.count, s, s / pattern.count / 2);
        break;
      case 'stripes':
        ctx.fillStyle = pattern.second;
        ctx.beginPath();
        ctx.moveTo(0, 0);
        ctx.lineTo(s / 2, 0);
        ctx.lineTo(0, s / 2);
        ctx.closePath();
        ctx.moveTo(s, 0);
        ctx.lineTo(s, s / 2);
        ctx.lineTo(s / 2, s);
        ctx.lineTo(0, s);
        ctx.closePath();
        ctx.fill();
        break;
    }
    texture.update();
    texture.wrapU = Texture.WRAP_ADDRESSMODE;
    texture.wrapV = Texture.WRAP_ADDRESSMODE;
    texture.uScale = 1 / pattern.tileMeters;
    texture.vScale = 1 / pattern.tileMeters;
    texture.anisotropicFilteringLevel = 4;
    return texture;
  }
}
